import React from "react";
import { usePublicPageSection } from "../../../../hooks/usePublicPageSection";
import { resolveMediaUrl } from "../../../../utils/mediaUrl";
import { useLanguage } from "../../../../context/LanguageContext";
import "./Hero.css";

const requireField = (fields, key) => {
  const value = fields?.[key];
  if (value == null || String(value).trim() === "") {
    throw new Error(`About hero field "${key}" is missing from the API response.`);
  }
  return value;
};

const requireMediaUrl = (section, groupKey) => {
  const item = section.media?.groups?.[groupKey]?.[0] || null;
  if (!item?.url) {
    throw new Error(`About hero media group "${groupKey}" is missing from the API response.`);
  }
  return resolveMediaUrl(item.url);
};

const buildModel = (section) => {
  const f = section.fields || {};
  return {
    badge: requireField(f, "badge"),
    title: requireField(f, "title"),
    subtitle: requireField(f, "subtitle"),
    missionHeading: requireField(f, "missionHeading"),
    missionText: requireField(f, "missionText"),
    missionImageAlt: requireField(f, "missionImageAlt"),
    visionHeading: requireField(f, "visionHeading"),
    visionText: requireField(f, "visionText"),
    visionImageAlt: requireField(f, "visionImageAlt"),
    missionIcon: requireMediaUrl(section, "mission-icon"),
    missionImage: requireMediaUrl(section, "mission-image"),
    visionIcon: requireMediaUrl(section, "vision-icon"),
    visionImage: requireMediaUrl(section, "vision-image"),
  };
};

const Hero = () => {
  const { meta } = useLanguage();
  const { model, error, isLoading } = usePublicPageSection("about", "hero", buildModel);

  if (isLoading) {
    return (
      <section className="about-hero" dir={meta.dir}>
        <p style={{ color: "#64748b", fontSize: 14 }}>Loading…</p>
      </section>
    );
  }

  if (error || !model) {
    return (
      <section className="about-hero" dir={meta.dir}>
        <p style={{ color: "#b91c1c", fontSize: 14 }}>
          {error || "About hero unavailable."}
        </p>
      </section>
    );
  }

  return (
    <section className="about-hero" dir={meta.dir}>
      <div className="about-hero__intro">
        <span className="about-hero__badge">{model.badge}</span>
        <h1 className="about-hero__title">{model.title}</h1>
        <p className="about-hero__subtitle">{model.subtitle}</p>
      </div>

      <div className="about-hero__rows">
        <div className="about-hero__row">
          <div className="about-hero__copy">
            <div className="about-hero__icon">
              <img src={model.missionIcon} alt="" aria-hidden="true" />
            </div>
            <h2 className="about-hero__heading">{model.missionHeading}</h2>
            <p className="about-hero__text">{model.missionText}</p>
          </div>

          <div className="about-hero__media">
            <img
              src={model.missionImage}
              alt={model.missionImageAlt}
              className="about-hero__image"
            />
            <div className="about-hero__overlay" aria-hidden="true" />
          </div>
        </div>

        <div className="about-hero__row about-hero__row--reverse">
          <div className="about-hero__media">
            <img
              src={model.visionImage}
              alt={model.visionImageAlt}
              className="about-hero__image"
            />
            <div className="about-hero__overlay" aria-hidden="true" />
          </div>

          <div className="about-hero__copy">
            <div className="about-hero__icon">
              <img src={model.visionIcon} alt="" aria-hidden="true" />
            </div>
            <h2 className="about-hero__heading">{model.visionHeading}</h2>
            <p className="about-hero__text">{model.visionText}</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
